import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "@commons/Modal";
import TextInput from "@commons/Input/Text";
import { usePage } from "@hooks/usePage";
import SelectInput from "@commons/Input/Select";
import {
  GENDER_OPTIONS,
  type MarkingType,
} from "@typings/entities/individuals";
import type { TaxonomicGroup } from "@typings/entities/taxonomicGroup";
import ComboBox from "@commons/Input/ComboBox";
import { searchTaxonomic } from "@services/taxonomicGroups/search";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import { useEffect } from "react";
import { globalMutate } from "@utils/swr";
import { useGetIndividualById } from "@services/individuals/getById";
import { updateIndividual } from "@services/individuals/update";
import type { UpdateIndividualDTO } from "@services/individuals/update/type";
import {
  individualEditSchema,
  individualEditDefaultValues,
  type IndividualEditFormData,
} from "../schemas/individualEditSchema";

function ModalEditIndividual() {
  const { isModalOpen, closeModal, setIsLoading, setDataModal, dataModal } =
    usePage();
  const open = isModalOpen("editIndividual");

  const id = dataModal?.id as string;
  const { data: individual, isLoading, error } = useGetIndividualById(id);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<IndividualEditFormData>({
    resolver: zodResolver(individualEditSchema),
    defaultValues: individualEditDefaultValues,
  });

  useEffect(() => {
    if (open && individual) {
      setValue("clinicalRecord", individual.clinicalRecord);
      setValue("commonName", individual.commonName);
      setValue("ufuName", individual.ufuName);
      setValue("gender", individual.gender);
      setValue("markingType", individual.markingType);
      setValue("marking", individual.marking || "");

      if (individual.taxonomicGroup) {
        setValue("species", {
          label:
            individual.taxonomicGroup.species ||
            individual.taxonomicGroup.family ||
            individual.taxonomicGroup.genus ||
            individual.taxonomicGroup.order ||
            "Sem identificação",
          value: individual.taxonomicGroup.id,
        });
      }
    } else if (!open) {
      reset();
    }
  }, [open, individual, setValue, reset]);

  const markingType = watch("markingType");
  const isMarkingDisabled = !markingType || markingType.trim() === "";

  useEffect(() => {
    if (isMarkingDisabled) {
      setValue("marking", "");
    }
  }, [isMarkingDisabled, setValue]);

  const onSubmit = async (data: IndividualEditFormData) => {
    try {
      setIsLoading(true);

      const payload: UpdateIndividualDTO = {
        clinicalRecord: data.clinicalRecord,
        commonName: data.commonName,
        ufuName: data.ufuName,
        gender: data.gender,
        taxonomicGroupId: data.species.value,
        markingType: (data.markingType?.trim() || null) as MarkingType | null,
        marking: data.marking?.trim() || null,
      };

      await updateIndividual(id, payload);

      toast.success("Indivíduo atualizado com sucesso");

      await globalMutate([
        "fetch-get-individuals",
        "fetch-individuals-geojson",
        `fetch-individual-${id}`,
      ]);

      handleClose();
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError ? error.message : "Erro ao atualizar indivíduo";
      toast.error(title);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setDataModal({});
    closeModal();
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      heading="Editar Indivíduo"
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel="Salvar"
      cancelLabel="Cancelar"
      className="max-w-3xl"
      isLoading={isLoading}
      error={!!error}
    >
      <form className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          name="clinicalRecord"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Nº Ficha Clínica"
              fullWidth
              error={!!errors.clinicalRecord}
              helperText={errors.clinicalRecord?.message}
            />
          )}
        />

        <Controller
          name="commonName"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Nome Comum"
              fullWidth
              error={!!errors.commonName}
              helperText={errors.commonName?.message}
            />
          )}
        />

        <Controller
          name="ufuName"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Nome UFU"
              fullWidth
              error={!!errors.ufuName}
              helperText={errors.ufuName?.message}
            />
          )}
        />

        <Controller
          name="gender"
          control={control}
          render={({ field }) => (
            <SelectInput
              {...field}
              label="Sexo"
              options={GENDER_OPTIONS}
              size="small"
              fullWidth
              value={field.value || ""}
              error={!!errors.gender}
              helperText={errors.gender?.message}
            />
          )}
        />

        <Controller
          name="markingType"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Tipo de Marcação"
              fullWidth
              value={field.value || ""}
              error={!!errors.markingType}
              helperText={errors.markingType?.message}
            />
          )}
        />

        <div
          className={isMarkingDisabled ? "opacity-50 pointer-events-none" : ""}
        >
          <Controller
            name="marking"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Marcação"
                fullWidth
                error={!!errors.marking}
                helperText={
                  errors.marking?.message ||
                  (isMarkingDisabled
                    ? "Desabilitado quando tipo de marcação está vazio"
                    : "")
                }
                disabled={isMarkingDisabled}
              />
            )}
          />
        </div>

        <div className="md:col-span-2">
          <Controller
            name="species"
            control={control}
            render={({ field }) => (
              <ComboBox<TaxonomicGroup>
                {...field}
                label="Nome Científico / Grupo Taxonômico"
                value={field.value ?? null}
                onChange={(option) => field.onChange(option ?? null)}
                searchService={searchTaxonomic}
                error={!!errors.species}
                helperText={errors.species?.message}
              />
            )}
          />
        </div>
      </form>
    </Modal>
  );
}

export default ModalEditIndividual;
